import { getNowPlainDateISO } from '@/lib/time-zones.js';
// ========== React & Libraries ==========
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { ChevronDown } from 'lucide-react';

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import { coachGradeOptions, coachGradeSchema, CsvCellValue, isAirAdmin } from '@airreading/common';
import {
  type FullCoach,
  coachAttendanceGradeSchema,
  getCoachesSortOrderSchema,
  GetCoachesSortableColumn,
  getCoachesSortableColumnSchema,
  coachStatusSchema,
  coachSubStatusSchema,
  CoachSubStatus,
  classEligibilitySchema,
  coachOverallGradeSchema,
  CoachStatus,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { getCoaches, EmptyArray } from '@/lib/api-calls.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import {
  coachStatusGroupedOptions,
  getCoachAttendanceGradeLabel,
  getCoachAttendanceGradeOptions,
  getCoachStatusLabel,
  classEligibilityOptions,
  getCoachOverallGradeOptions,
} from '@/lib/coach-utils.js';
import { CoachOverallGradeBadge } from '@/components/coaches/coach-overall-grade-badge.js';
import { exportToCSV, buildClientExportColumns } from '@/lib/csv-utils.js';
import { airColumnHelper } from '@/lib/tables.js';
import { useSorting } from '@/lib/sorting.js';

// ========== UI Components ==========
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

// ========== Table Components ==========
import { EntityTable, createTableRowSelectionCell } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu.js';
import { TableFilterDropdownSubItems } from '@/components/table/table-filter-dropdown-sub-items.js';

// ========== Layout Components ==========
import { canCreateCoachUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { InfoPage } from '@/components/info-page.js';

// ========== Users/Teachers Components ==========
import { CreateCoach } from '@/routes/_layout/teachers/-components/create-coach.js';
import { CreateCoachesByCsv } from '@/routes/_layout/-components/create-coaches-by-csv.js';
import { NeedsReviewToggle } from '@/routes/_layout/teachers/-components/toggle-needs-review.js';
import { CoachStatusCell } from '@/routes/_layout/teachers/-components/coach-status-cell.js';
import { coachesSectionTabs } from './-components/tabs.js';

const usersSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  teachingGrades: z.array(coachGradeSchema).optional(),
  attendanceGrades: z.array(coachAttendanceGradeSchema).optional(),
  sortBy: getCoachesSortOrderSchema.optional(),
  statuses: z.array(coachStatusSchema).optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  overallGrades: z.array(coachOverallGradeSchema).optional(),
});

export const Route = createFileRoute('/_layout/teachers/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: UsersIndex,
  validateSearch: usersSearchSchema,
});

const columnHelper = airColumnHelper<FullCoach>(getCoachesSortableColumnSchema);
const userTableColumns = [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor((row) => row, {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ getValue }) => {
      const { fullNameOrEmail, id } = getValue();
      return (
        <Link to={'/teachers/$teacherId'} params={{ teacherId: id }}>
          {fullNameOrEmail}
        </Link>
      );
    },
  }),

  columnHelper.accessor('overallGrade', {
    id: 'overallGrade',
    header: 'Overall grade',
    cell: ({ getValue }) => <CoachOverallGradeBadge grade={getValue()} />,
  }),

  columnHelper.accessor('qualityGrade', {
    id: 'teachingGrade',
    header: 'Teaching grade',
  }),

  columnHelper.accessor('attendanceGrade', {
    id: 'attendanceGrade',
    header: 'Attendance grade',
    cell: ({ getValue }) => {
      const attendanceGrade = getValue();
      return getCoachAttendanceGradeLabel(attendanceGrade);
    },
  }),

  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),

  columnHelper.accessor((row) => row, {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const { status, subStatuses } = getValue();
      return <CoachStatusCell status={status} subStatuses={subStatuses} />;
    },
  }),

  columnHelper.accessor('activeRegularClassCount', {
    id: 'activeRegularClassCount',
    header: 'Active regular classes',
  }),

  columnHelper.accessor('activeOneTimeClassCount', {
    id: 'activeOneTimeClassCount',
    header: 'Active one-time classes',
  }),

  columnHelper.accessor('needsReview', {
    id: 'needsReview',
    header: 'Needs review',
    cell: ({ row }) => <NeedsReviewToggle coach={row.original} />,
  }),
];

function UsersIndex() {
  const { actingUser } = useSession();
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      overallGrades: [[overallGradeFilter, activeOverallGradeIds], setOverallGradeFilter],
      teachingGrades: [[gradeFilter, activeGradeFilter], setGradeFilter],
      attendanceGrades: [[attendanceGradeFilter, activeAttendanceGradeFilter], setAttendanceGradeFilter],
      classEligibilities: [[classEligibilityFilter, activeClassEligibilityFilter], setClassEligibilityFilter],
      statuses: [[statusIds, activeStatusIds], setStatusIds],
      subStatuses: [[subStatusIds, activeSubStatusIds], setSubStatusIds],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(
    Route.id,
    ['query', 'teachingGrades', 'attendanceGrades', 'classEligibilities', 'statuses', 'subStatuses', 'overallGrades'],
    ['teachingGrades', 'attendanceGrades'],
  );
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetCoachesSortableColumn>([sortBy, didChangeSortBy]);

  const { data, isPending, error, refetch } = useQuery(
    getCoaches({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        teachingGrades: activeGradeFilter,
        overallGrades: activeOverallGradeIds,
        attendanceGrades: activeAttendanceGradeFilter,
        classEligibilities: activeClassEligibilityFilter,
        statuses: activeStatusIds,
        subStatuses: activeSubStatusIds,
      },
      sortBy,
    }),
  );

  const coaches = useMemo(() => data?.coaches || EmptyArray, [data]);
  useErrorToast(error, 'Error loading teachers');

  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: coaches,
    columns: userTableColumns,
    state: {
      pagination: { pageIndex, pageSize },
      rowSelection,
      sorting,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search teachers by name or email"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
        data-testid="teachers-filter-input-search"
      />
      <TableFilterDropdown
        title="Overall Grade"
        options={getCoachOverallGradeOptions()}
        selectedValues={overallGradeFilter ?? []}
        onChange={setOverallGradeFilter}
      />
      <TableFilterDropdownSubItems<CoachStatus, CoachSubStatus>
        title="Status"
        options={coachStatusGroupedOptions}
        selectedValues={statusIds ?? []}
        selectedSubValues={subStatusIds ?? []}
        onChange={setStatusIds}
        onSubValuesChange={setSubStatusIds}
      />
      <TableFilterDropdown
        title="Eligibility"
        options={classEligibilityOptions}
        selectedValues={classEligibilityFilter ?? []}
        onChange={setClassEligibilityFilter}
      />
      <TableMoreFiltersMenu
        filterItems={[
          {
            title: 'Teaching Grade',
            filterComponent: (
              <TableFilterDropdown
                title="Teaching Grade"
                options={coachGradeOptions.map((g) => ({ label: g, value: g }))}
                selectedValues={gradeFilter ?? []}
                onChange={setGradeFilter}
              />
            ),
          },
          {
            title: 'Attendance Grade',
            filterComponent: (
              <TableFilterDropdown
                title="Attendance Grade"
                options={getCoachAttendanceGradeOptions()}
                selectedValues={attendanceGradeFilter ?? []}
                onChange={setAttendanceGradeFilter}
              />
            ),
          },
        ]}
        activeFiltersCount={activeFiltersCount}
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
    </>
  );

  const overrides: Partial<Record<GetCoachesSortableColumn, (row: FullCoach, index: number) => CsvCellValue>> = {
    fullNameOrEmail: (row) => row.fullNameOrEmail ?? null,
    status: (row) => getCoachStatusLabel(row.status) ?? null,
    qualityGrade: (row) => row.qualityGrade ?? null,
    needsReview: (row) => (row.needsReview ? 'Yes' : 'No'),
    email: (row) => row.email,
  };
  const exportColumns = useMemo(() => buildClientExportColumns(userTableColumns, overrides), [userTableColumns]);

  const bulkActions = (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
        const date = getNowPlainDateISO(actingUser.timeZone).toString();
        exportToCSV(selectedRows, exportColumns, `teachers_export_${date}.csv`);
      }}
    >
      Export teachers with all columns
    </DropdownMenuItem>
  );

  const createUsersMenu = canCreateCoachUsers(actingUser) && (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add teachers
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateCoach
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a teacher</DropdownMenuItem>}
        />
        <CreateCoachesByCsv
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        autoManagedColumnIds={['selection']}
        hasError={!!error}
        emptyState="No users found"
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
        bulkActionsMenuItems={bulkActions}
        topBarRightComponents={createUsersMenu}
      />
    </InfoPage>
  );
}
